import React from 'react';
import { Cpu, ChevronDown, ArrowRight, Shield, Zap, Globe, Award } from 'lucide-react';

/**
 * Enhanced Hero Component - Inspired by Smartsocs.com
 * - Gradient overlays
 * - Animated background patterns
 * - Clear value proposition
 * - Trust indicators
 */

const Hero: React.FC = () => {
    return (
        <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#0a1628] via-[#153860] to-[#0f2847]">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-30">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `
                            radial-gradient(circle at 20% 50%, rgba(85, 195, 189, 0.15) 0%, transparent 50%),
                            radial-gradient(circle at 80% 20%, rgba(0, 121, 194, 0.15) 0%, transparent 40%),
                            radial-gradient(circle at 60% 80%, rgba(238, 118, 35, 0.1) 0%, transparent 40%)
                        `
                    }}
                />
            </div>

            {/* Grid Pattern Overlay */}
            <div
                className="absolute inset-0 opacity-10"
                style={{
                    backgroundImage: 'linear-gradient(rgba(85, 195, 189, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(85, 195, 189, 0.3) 1px, transparent 1px)',
                    backgroundSize: '50px 50px'
                }}
            />

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 lg:py-40">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Left Content */}
                    <div>
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#55C3BD]/10 border border-[#55C3BD]/30 text-[#55C3BD] text-xs font-semibold uppercase tracking-wider mb-8">
                            <Cpu size={14} /> Your Turnkey Engineering Partner
                        </div>

                        {/* Main Headline */}
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            Engineering
                            <span className="text-[#55C3BD]"> Silicon </span>
                            Excellence,
                            <br />
                            <span className="text-white/90">From Concept to Chip</span>
                        </h1>

                        {/* Subtitle */}
                        <p className="text-lg md:text-xl text-white/70 mb-8 leading-relaxed max-w-xl">
                            LanceSoft delivers specialized VLSI/Semiconductor engineering solutions
                            - from RTL to GDSII, verification to validation, and embedded systems
                            to production support.
                        </p>

                        {/* CTA Buttons */}
                        <div className="flex flex-wrap gap-4 mb-12">
                            <a
                                href="#capabilities"
                                className="group px-8 py-4 bg-[#EE7623] text-white font-semibold rounded-lg flex items-center gap-3 hover:bg-[#ff8a3d] transition-all duration-300 shadow-lg shadow-[#EE7623]/25"
                            >
                                Explore Our Services
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </a>
                            <a
                                href="#contact"
                                className="px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-lg flex items-center gap-3 hover:bg-white/10 hover:border-white/50 transition-all duration-300"
                            >
                                Get In Touch
                            </a>
                        </div>

                        {/* Trust Indicators */}
                        <div className="flex flex-wrap gap-8 pt-8 border-t border-white/10">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-lg bg-[#55C3BD]/20 flex items-center justify-center">
                                    <Award size={24} className="text-[#55C3BD]" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-white">22+</div>
                                    <div className="text-xs text-white/60 uppercase tracking-wider">Years Experience</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-lg bg-[#55C3BD]/20 flex items-center justify-center">
                                    <Zap size={24} className="text-[#55C3BD]" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-white">5000</div>
                                    <div className="text-xs text-white/60 uppercase tracking-wider">Global Engineers</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Visual - Feature Cards */}
                    <div className="hidden lg:block relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#55C3BD]/20 to-[#0079C2]/10 rounded-3xl blur-3xl" />
                        <div className="relative grid grid-cols-2 gap-4">
                            {[
                                { icon: Shield, title: "Pre-Silicon", desc: "Architecture to Tape-out" },
                                { icon: Zap, title: "Post-Silicon", desc: "Validation & Debug" },
                                { icon: Cpu, title: "Design", desc: "RTL to GDSII" },
                                { icon: Globe, title: "Embedded", desc: "Firmware & Software" }
                            ].map((item, idx) => (
                                <div
                                    key={idx}
                                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-[#55C3BD]/50 transition-all duration-300 group"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#55C3BD] to-[#0079C2] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                        <item.icon size={24} className="text-white" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                                    <p className="text-sm text-white/60">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll Indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40 animate-bounce">
                <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                <ChevronDown size={24} />
            </div>
        </section>
    );
};

export default Hero;
